import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Grid,
  Chip,
  Divider,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Send as SendIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Person as PersonIcon,
  DirectionsCar as CarIcon,
  Assignment as AssignmentIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import bookingService from '../../services/bookingService';

const EmailProcessor = () => {
  const [emailContent, setEmailContent] = useState('');
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!emailContent.trim()) {
      setError('Please enter email content to process');
      return;
    }

    setProcessing(true);
    setError(null);
    setResult(null);

    try {
      const response = await bookingService.processBookingEmail(emailContent);
      setResult(response);
    } catch (err) {
      console.error('Processing error:', err);
      setError(
        err.response?.data?.error ||
        'Failed to process email. Please check the content and try again.'
      );
    } finally {
      setProcessing(false);
    }
  };

  const handleClear = () => {
    setEmailContent('');
    setResult(null);
    setError(null);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  const renderExtractedData = () => {
    if (!result?.extracted_data) return null;

    const data = result.extracted_data;

    return (
      <Grid container spacing={3}>
        {/* Customer Information */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 2 }}>
            <Box display="flex" alignItems="center" mb={2}>
              <PersonIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6">Customer Information</Typography>
            </Box>
            <Typography><strong>Name:</strong> {data.customer?.name || 'N/A'}</Typography>
            <Typography><strong>Email:</strong> {data.customer?.email || 'N/A'}</Typography>
            <Typography><strong>Phone:</strong> {data.customer?.phone || 'N/A'}</Typography>
          </Paper>
        </Grid>

        {/* Vehicle Information */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 2 }}>
            <Box display="flex" alignItems="center" mb={2}>
              <CarIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6">Vehicle Information</Typography>
            </Box>
            <Typography><strong>Make/Model:</strong> {data.vehicle?.make_model || 'N/A'}</Typography>
            <Typography><strong>Registration:</strong> {data.vehicle?.registration || 'N/A'}</Typography>
            {data.vehicle?.fuel && (
              <Typography><strong>Fuel Type:</strong> {data.vehicle.fuel}</Typography>
            )}
            {data.vehicle?.engine_size && (
              <Typography><strong>Engine Size:</strong> {data.vehicle.engine_size}</Typography>
            )}
          </Paper>
        </Grid>

        {/* Booking Details */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 2 }}>
            <Box display="flex" alignItems="center" mb={2}>
              <AssignmentIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6">Booking Details</Typography>
            </Box>
            <Typography><strong>Date:</strong> {data.booking_date?.date || 'N/A'}</Typography>
            <Typography><strong>Time:</strong> {data.booking_date?.time_of_day || 'N/A'}</Typography>
            <Typography><strong>Location:</strong> {data.location || 'N/A'}</Typography>
            <Typography><strong>Total Cost:</strong> {formatCurrency(data.total_cost || 0)}</Typography>

            {data.cancelled && (
              <Box mt={1}>
                <Chip
                  icon={<CancelIcon />}
                  label="CANCELLED"
                  color="error"
                  variant="outlined"
                />
                {data.reason && (
                  <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                    <strong>Reason:</strong> {data.reason}
                  </Typography>
                )}
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Work Required */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 2 }}>
            <Typography variant="h6" mb={2}>Work Required</Typography>
            {data.work_required?.length > 0 ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Service</TableCell>
                      <TableCell align="right">Cost</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.work_required.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{item.service}</TableCell>
                        <TableCell align="right">
                          {item.cost ? formatCurrency(item.cost) : 'TBC'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography color="textSecondary">No work specified</Typography>
            )}
          </Paper>
        </Grid>

        {/* Advisories */}
        {data.advisories?.length > 0 && (
          <Grid item xs={12}>
            <Paper elevation={2} sx={{ p: 2 }}>
              <Box display="flex" alignItems="center" mb={2}>
                <WarningIcon color="warning" sx={{ mr: 1 }} />
                <Typography variant="h6">Advisories</Typography>
              </Box>
              {data.advisories.map((advisory, index) => (
                <Box key={index} mb={1}>
                  <Chip
                    label={advisory.severity}
                    color={
                      advisory.severity === 'MAJOR' ? 'error' :
                      advisory.severity === 'MINOR' ? 'warning' : 'info'
                    }
                    size="small"
                    sx={{ mr: 1 }}
                  />
                  <Typography variant="body2" component="span">
                    {advisory.description}
                  </Typography>
                </Box>
              ))}
            </Paper>
          </Grid>
        )}

        {/* Notes */}
        {data.notes && (
          <Grid item xs={12}>
            <Paper elevation={2} sx={{ p: 2 }}>
              <Typography variant="h6" mb={1}>Notes</Typography>
              <Typography variant="body2">{data.notes}</Typography>
            </Paper>
          </Grid>
        )}
      </Grid>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        BookMyGarage Email Processor
      </Typography>
      <Typography variant="body1" color="textSecondary" mb={3}>
        Paste BookMyGarage email content below to automatically extract booking information and save to the CRM database.
      </Typography>

      <Grid container spacing={3}>
        {/* Email Input Form */}
        <Grid item xs={12} lg={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Email Content
              </Typography>

              <form onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  multiline
                  rows={12}
                  variant="outlined"
                  placeholder="Paste the BookMyGarage email content here..."
                  value={emailContent}
                  onChange={(e) => setEmailContent(e.target.value)}
                  disabled={processing}
                  sx={{ mb: 2 }}
                />

                <Box display="flex" gap={2}>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={processing || !emailContent.trim()}
                    startIcon={processing ? <CircularProgress size={20} /> : <SendIcon />}
                    sx={{ minWidth: 120 }}
                  >
                    {processing ? 'Processing...' : 'Process Email'}
                  </Button>

                  <Button
                    variant="outlined"
                    onClick={handleClear}
                    disabled={processing}
                  >
                    Clear
                  </Button>
                </Box>
              </form>
            </CardContent>
          </Card>
        </Grid>

        {/* Results Panel */}
        <Grid item xs={12} lg={6}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {result && (
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                  <Typography variant="h6">
                    Processing Successful
                  </Typography>
                </Box>

                <Alert severity="success" sx={{ mb: 2 }}>
                  {result.message}
                </Alert>

                <Typography variant="body2" color="textSecondary" mb={2}>
                  <strong>Action:</strong> {result.action} |
                  <strong> Booking ID:</strong> {result.booking_id} |
                  <strong> Customer ID:</strong> {result.customer_id} |
                  <strong> Vehicle ID:</strong> {result.vehicle_id}
                  {result.advisories_created > 0 && (
                    <span> | <strong>Advisories:</strong> {result.advisories_created}</span>
                  )}
                </Typography>

                <Divider sx={{ my: 2 }} />

                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">Extracted Data Details</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {renderExtractedData()}
                  </AccordionDetails>
                </Accordion>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default EmailProcessor;